import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { FormProvider, useFieldArray, useForm, useWatch, type SubmitHandler } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import GenericModal from '../../components/GenericModal'
import DefaultModalActions from '../../components/GenericModal/DefaultModalActions'
import GenericButton from '../../components/GenericButton'
import { ButtonType } from '../../components/GenericButton/styles'
import { notification } from '../../components/Notifications'
import { ReportPermissionManager } from '../../APIManager'
import { capitalizeFirst } from '../../utils/utils'
import { type ApiError } from '../../models/Common'
import { grey } from '../../colors'
import PermissionGroupCard from './PermissionGroupCard'
import InheritedGroupCard from './InheritedGroupCard'
import {
  type PermissionCardForm,
  type ReportPermissionGroup,
  type ReportPermissionScope,
  type ReportPermissionsFormData
} from './types'
import {
  areCardsValid,
  cardsToPayload,
  groupsToCards,
  inheritedCardsForSlot,
  makeEmptyCard,
  takenRoleIds,
  validateCards
} from './utils'

export interface ReportPermissionsFeatureProps {
  scope: ReportPermissionScope
  /** Distribution series id for `series` scope; slot id for `slot` scope. */
  scopeId: string
  /** Series id used to load the inherited baseline; required for `slot` scope. */
  seriesId?: string
  /** Entity name shown in the subtitle (series title or instance term). */
  scopeLabel?: string
  canEdit: boolean
  isOpen: boolean
  handleCloseModal: () => void
}

const seriesKey = (id: string): unknown[] => ['reportPermissions', 'distributionSeries', id]
const slotKey = (id: string): unknown[] => ['reportPermissions', 'slot', id]

const ReportPermissionsFeature: React.FC<ReportPermissionsFeatureProps> = ({
  scope,
  scopeId,
  seriesId,
  scopeLabel,
  canEdit,
  isOpen,
  handleCloseModal
}) => {
  const queryClient = useQueryClient()
  const isSlot = scope === 'slot'
  const baselineSeriesId = isSlot ? (seriesId ?? '') : scopeId

  const methods = useForm<ReportPermissionsFormData>({ defaultValues: { cards: [] } })
  const { control, handleSubmit, reset } = methods
  const { fields, append, remove } = useFieldArray({ control, name: 'cards' })

  const watchedCards = useWatch({ control, name: 'cards' }) ?? []
  const cardErrors = validateCards(watchedCards)

  // Validation messages surface only after a Save attempt, and clear on the next edit
  // (re-appearing only if the user tries to save again while still invalid).
  const [submitAttempted, setSubmitAttempted] = useState(false)
  useEffect(() => {
    const subscription = methods.watch(() => { setSubmitAttempted(false) })
    return () => { subscription.unsubscribe() }
  }, [methods])

  const seriesQuery = useQuery({
    queryKey: seriesKey(baselineSeriesId),
    queryFn: async (): Promise<ReportPermissionGroup[]> =>
      (await ReportPermissionManager.getSeriesReportPermissions(baselineSeriesId)).data,
    enabled: isOpen && baselineSeriesId !== '',
    retry: false
  })

  const slotQuery = useQuery({
    queryKey: slotKey(scopeId),
    queryFn: async (): Promise<ReportPermissionGroup[]> =>
      (await ReportPermissionManager.getSlotReportPermissions(scopeId)).data,
    enabled: isOpen && isSlot && scopeId !== '',
    retry: false
  })

  const isLoading =
    (seriesQuery.isLoading && seriesQuery.fetchStatus !== 'idle') ||
    (isSlot && slotQuery.isLoading && slotQuery.fetchStatus !== 'idle')

  // Series default groups — used to render the read-only "using defaults" view on a slot,
  // and copied into editable cards when the admin chooses to override.
  const seriesDefaultCards = useMemo(
    () => groupsToCards(seriesQuery.data ?? []),
    [seriesQuery.data]
  )

  // Seed the form from server data exactly once per open. A ref guard prevents later
  // refetches (window-focus, reconnect, dev hot-reload) from re-running reset() and
  // wiping edits in progress. Editable cards are this scope's own groups; a slot with no
  // groups is "using the Survey Project defaults" (whole-scope inherit).
  const seededRef = useRef(false)
  useEffect(() => {
    if (!isOpen) { seededRef.current = false; return }
    if (seededRef.current) return
    if (seriesQuery.isLoading) return
    if (isSlot && slotQuery.isLoading) return
    seededRef.current = true
    const ownGroups = isSlot ? (slotQuery.data ?? []) : (seriesQuery.data ?? [])
    reset({ cards: groupsToCards(ownGroups) })
  }, [isOpen, isSlot, seriesQuery.isLoading, slotQuery.isLoading, seriesQuery.data, slotQuery.data, reset])

  const saveMutation = useMutation({
    mutationFn: async (cards: PermissionCardForm[]) => {
      const payload = cardsToPayload(cards)
      return isSlot
        ? await ReportPermissionManager.saveSlotReportPermissions(scopeId, payload)
        : await ReportPermissionManager.saveSeriesReportPermissions(scopeId, payload)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: isSlot ? slotKey(scopeId) : seriesKey(scopeId) })
    }
  })

  const onSubmit: SubmitHandler<ReportPermissionsFormData> = async (data) => {
    if (!areCardsValid(data.cards)) {
      setSubmitAttempted(true)
      notification('Please resolve the highlighted issues before saving.', { variant: 'error' })
      return
    }
    try {
      await saveMutation.mutateAsync(data.cards)
      notification('Report permissions saved successfully.', { variant: 'success' })
      handleCloseModal()
    } catch (error) {
      // The api interceptor rejects with a structured { status, message } object.
      const apiError = error as Partial<ApiError>
      if (apiError.status === 404) {
        // The scope entity is gone (deleted while the modal was open) — close the modal;
        // there's nothing left to save into.
        notification('This survey project no longer exists. Please refresh and try again.', { variant: 'error' })
        handleCloseModal()
        return
      }
      // 422 validation / 409 conflict — surface the backend message when present.
      notification(apiError.message ?? 'Failed to save report permissions.', { variant: 'error' })
    }
  }

  // Slot inheritance is per-role: project defaults minus roles the slot already overrides.
  // Recomputed live from the form so overriding a role moves it out of the inherited list.
  const inheritedCards = isSlot ? inheritedCardsForSlot(seriesDefaultCards, watchedCards) : []

  // Override one inherited group: seed an editable slot card from its (currently-inherited)
  // roles + values. Those roles then drop out of the inherited section.
  const handleOverrideGroup = (card: PermissionCardForm): void => {
    append({ ...card, groupId: undefined })
  }

  const subtitle = scopeLabel != null && scopeLabel !== ''
    ? `Permission Settings for "${capitalizeFirst(scopeLabel)}"`
    : 'Permission Settings'

  const description = isSlot
    ? 'Report permissions control what each role can see in reports for this term. Overrides set for a specific term of the survey project will take precedence over the project’s settings here; any role you don’t override will inherit from the project’s settings.'
    : 'Report permissions control what each role can see in reports. These settings apply to every term in this Survey Project, unless a term overrides them. Choose which roles each setting applies to, then set whether they have full access, view-only access, or no access to results.'

  return (
    <GenericModal
      isOpen={isOpen}
      maxWidth="sm"
      onClose={handleCloseModal}
      title={
        <Box>
          <Typography fontWeight={700} fontSize="18px">Report Permission Settings</Typography>
          <Typography fontSize="14px" color={grey[850]} sx={{ mt: 1, fontWeight: 400 }}>
            {description}
          </Typography>
        </Box>
      }
      action={
        <DefaultModalActions
          destructiveButtonProps={{ onClick: handleCloseModal, text: 'Cancel', type: 'button' }}
          confirmButtonProps={{
            text: 'Save',
            type: 'button',
            hideButton: !canEdit,
            loading: saveMutation.isPending,
            onClick: () => { void handleSubmit(onSubmit)() }
          }}
        />
      }
    >
      {isLoading
        ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          )
        : (
          <FormProvider {...methods}>
            <form>
              <Typography sx={{ fontSize: '16px', color: grey[250], fontWeight: 600, mb: 3 }}>
                {subtitle}
              </Typography>

              {/* Slot: inherited project defaults (per-role), each overridable. */}
              {isSlot && inheritedCards.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '14px', color: grey[250], fontWeight: 600, mb: 2 }}>
                    Inherited from Survey Project
                  </Typography>
                  {inheritedCards.map((card, i) => (
                    <InheritedGroupCard
                      key={`inherited-${card.groupId ?? i}`}
                      card={card}
                      canEdit={canEdit}
                      onOverride={() => { handleOverrideGroup(card) }}
                    />
                  ))}
                </Box>
              )}

              {isSlot && inheritedCards.length === 0 && seriesDefaultCards.length === 0 && fields.length === 0 && (
                <Typography color={grey[150]} sx={{ mb: 2 }}>
                  The Survey Project has no permission settings yet. Add term-specific permissions below, or reports
                  use the pre-existing behavior.
                </Typography>
              )}

              {/* This scope's own groups: slot overrides/additions, or the series defaults themselves. */}
              {isSlot && fields.length > 0 && (
                <Typography sx={{ fontSize: '14px', color: grey[250], fontWeight: 600, mb: 2 }}>
                  Term-Specific Overrides
                </Typography>
              )}
              {fields.map((field, index) => (
                <PermissionGroupCard
                  key={field.id}
                  index={index}
                  canEdit={canEdit}
                  cardError={cardErrors[index]}
                  showErrors={submitAttempted}
                  onRemove={remove}
                  taken={takenRoleIds(watchedCards, index)}
                />
              ))}

              {!isSlot && fields.length === 0 && (
                <Typography color={grey[150]} sx={{ py: 4, textAlign: 'center' }}>
                  No report permissions configured.{canEdit ? ' Add a permission group to get started.' : ''}
                </Typography>
              )}

              {canEdit && (
                <GenericButton
                  buttonType={ButtonType.Secondary}
                  startIcon={<AddIcon />}
                  label="Add Report Permissions"
                  onClick={() => { append(makeEmptyCard()) }}
                  sx={{ width: '100%', mt: 2 }}
                />
              )}
            </form>
          </FormProvider>
          )}
    </GenericModal>
  )
}

export default ReportPermissionsFeature
